import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { loginWithGoogle, useAuth } from "@/lib/auth";
import { BRAND } from "@/lib/constants";
import { OtpVerify } from "@/components/OtpVerify";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [otp, setOtp] = useState(null); // {pending_token, phone_hint} — dispatcher 2FA step

  useEffect(() => {
    if (!user) return;
    const dest = user.role === "dispatcher" ? "/dispatcher" : user.role === "driver" ? "/driver" : "/customer";
    nav(dest, { replace: true });
  }, [user, nav]);

  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "signin") {
        const r = await api.post("/auth/login", { email, password });
        if (r.data.otp_required) {
          setOtp({ pending_token: r.data.pending_token, phone_hint: r.data.phone_hint });
          return;
        }
        toast.success(`Welcome back, ${r.data.name}`);
      } else {
        if (!name.trim()) throw new Error("Please enter your name.");
        const r = await api.post("/auth/register", { email, password, name, phone: phone || undefined });
        if (r.data.otp_required) {
          setOtp({ pending_token: r.data.pending_token, phone_hint: r.data.phone_hint });
          return;
        }
        toast.success("Account created. You're signed in.");
      }
      await refresh();
      const me = await api.get("/auth/me");
      const dest = me.data.role === "dispatcher" ? "/dispatcher"
                : me.data.role === "driver"     ? "/driver"
                : "/customer";
      nav(dest, { replace: true });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="public-light bg-[#050505] min-h-screen text-[#F5F2EB]">
      <Navbar/>
      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-md mx-auto border border-white/10 bg-[#0A0A0A] p-8 md:p-12">
          {otp ? (
            <OtpVerify
              pendingToken={otp.pending_token}
              phoneHint={otp.phone_hint}
              onCancel={() => { setOtp(null); setPassword(""); }}
              onSuccess={async (u) => {
                await refresh();
                const dest = u.role === "dispatcher" ? "/dispatcher" : u.role === "driver" ? "/driver" : "/customer";
                nav(dest, { replace: true });
              }}
            />
          ) : (<>
          <div className="overline mb-3">Account access</div>
          <h1 className="font-serif text-3xl mb-2">{mode === "signin" ? "Welcome back" : "Create account"}</h1>
          <p className="text-[#A1A1AA] text-sm mb-2">
            {mode === "signin" ? "Sign in to your account or continue with Google." : "Sign up to book rides, save your address, and track invoices."}
          </p>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#71717A] mb-6">
            Booking for a company? <Link data-testid="corp-signup-top" to="/corporate/signup" className="text-[#D4AF37] hover:underline">Corporate account →</Link>
          </div>

          <div className="flex border-b border-white/10 mb-6">
            <button
              data-testid="tab-signin"
              onClick={() => { setMode("signin"); setErr(""); }}
              className={`flex-1 py-3 text-[11px] uppercase tracking-[0.22em] border-b-2 transition ${mode === "signin" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A1A1AA] hover:text-white"}`}
            >Sign in</button>
            <button
              data-testid="tab-signup"
              onClick={() => { setMode("signup"); setErr(""); }}
              className={`flex-1 py-3 text-[11px] uppercase tracking-[0.22em] border-b-2 transition ${mode === "signup" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A1A1AA] hover:text-white"}`}
            >Sign up</button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <>
                <input data-testid="signup-name" type="text" required value={name} onChange={e => setName(e.target.value)}
                  placeholder="Full name"
                  className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none"/>
                <input data-testid="signup-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                  placeholder="Phone (optional)"
                  className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none"/>
              </>
            )}
            <input data-testid="login-email" type="email" autoComplete="email" required value={email}
              onChange={e => setEmail(e.target.value)} placeholder="you@email.com"
              className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none"/>
            <input data-testid="login-password" type="password" required value={password}
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              onChange={e => setPassword(e.target.value)} placeholder="Password (min 8 chars)"
              className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none"/>

            {err && <div data-testid="login-error" className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 p-2.5">{err}</div>}

            <button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full bg-[#D4AF37] text-black py-3.5 font-medium uppercase tracking-[0.18em] text-xs hover:opacity-90 disabled:opacity-60 transition">
              {busy ? "…" : mode === "signin" ? "Sign in" : "Create account"}
            </button>
          </form>

          {mode === "signin" && (
            <div className="text-right mt-3">
              <Link data-testid="forgot-pwd-link" to="/forgot-password" className="text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#D4AF37]">
                Forgot password?
              </Link>
            </div>
          )}

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10"/>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">or</div>
            <div className="flex-1 h-px bg-white/10"/>
          </div>

          <button
            data-testid="google-login-button"
            type="button"
            onClick={() => loginWithGoogle("/dashboard")}
            className="w-full bg-white text-black py-3.5 font-medium uppercase tracking-[0.18em] text-xs hover:bg-[#F5F2EB] transition"
          >
            Continue with Google
          </button>

          <Link
            data-testid="driver-otp-login-link"
            to="/driver-login"
            className="block w-full text-center border border-[#D4AF37]/40 text-[#D4AF37] py-3.5 font-medium uppercase tracking-[0.18em] text-xs hover:bg-[#D4AF37]/10 transition mt-3"
          >
            Driver? Sign in with a text code
          </Link>

          <Link
            data-testid="customer-otp-login-link"
            to="/phone-login"
            className="block w-full text-center border border-white/20 text-[#F5F2EB] py-3.5 font-medium uppercase tracking-[0.18em] text-xs hover:border-[#D4AF37] hover:text-[#D4AF37] transition mt-3"
          >
            Passenger? Sign in with your phone
          </Link>

          <div className="mt-8 pt-6 border-t border-white/5 text-[11px] text-[#71717A] leading-relaxed">
            New accounts default to <span className="text-[#D4AF37]">customer</span>. Drivers and dispatchers are promoted by an admin.
          </div>
          <div className="mt-3 text-[11px] text-[#A1A1AA]">
            Booking for a company? <Link data-testid="corp-signup-link" to="/corporate/signup" className="text-[#D4AF37] hover:underline">Sign up for a corporate account →</Link>
          </div>
          <div className="mt-3 text-[11px] text-[#52525B]">
            Need help? Call {BRAND.phones[0]}
          </div>
          </>)}
        </div>
      </section>
      <Footer/>
    </div>
  );
}
